import {
  resolveNextActionTimestamp,
  type CustomerInteractionActivityType,
  type CustomerInteractionNextAction,
  type TeammateAccountGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { DateTime } from "luxon";
import type { FC } from "react";

/**
 * The two columns the voice agent's customer-interactions table adds: what has
 * already happened on this interaction, and what is about to. Both read
 * `grading.call_activity`, which the API only populates when the caller asks
 * for it.
 */
export function buildCallActivityColumns(
  timezone: string,
): readonly EntityTableColumnDefinition<TeammateAccountGrading>[] {
  return [
    {
      id: "mostRecentActivity",
      className: "w-0",
      header: "Most recent activity",
      renderCell: (grading) => {
        // History is newest first, so the head of it is the latest thing that
        // happened — which is exactly what this column reports.
        const mostRecent = grading.call_activity?.history.at(0);
        return mostRecent ? (
          <span className="text-sm whitespace-nowrap text-foreground">
            {callActivityLabel[mostRecent.type]}
          </span>
        ) : (
          <EmptyCell />
        );
      },
    },
    {
      id: "nextAction",
      className: "w-0",
      header: "Next action",
      renderCell: (grading) => {
        const nextAction = grading.call_activity?.next_action;
        if (!nextAction) {
          return <EmptyCell />;
        }

        const timestamp = resolveNextActionTimestamp(nextAction);

        return (
          <div className="flex flex-col gap-0.5 whitespace-nowrap">
            <span className="text-sm text-foreground">
              {nextActionLabel[nextAction.type]}
            </span>
            <span className="text-xs text-muted-foreground">
              {nextActionTimePrefix[nextAction.type]}
              <time dateTime={timestamp}>
                {formatCallActivityTimestamp(timestamp, timezone)}
              </time>
              {/* The agent decides when to call, so it has no attempts to count. */}
              {nextAction.type === "monitoring" ||
              nextAction.type === "monitoring_paused"
                ? null
                : ` — Attempt ${nextAction.attempt_number}`}
            </span>
          </div>
        );
      },
    },
  ];
}

export const callActivityLabel: Record<
  CustomerInteractionActivityType,
  string
> = {
  call_started: "Call started",
  call_answered: "Call answered",
  call_not_answered: "Call not answered",
  call_canceled: "Call canceled",
  call_forwarded: "Call forwarded",
  call_failed: "Call failed",
};

/**
 * Why a call failed, for the codes an operator can act on. Anything absent
 * shows as a bare "Call failed" rather than a raw code.
 */
export const callFailureReasonLabel: Record<string, string> = {
  expired: "the call was no longer useful",
  window_past_expiry: "the next calling window opened too late",
  retries_exhausted: "no attempts left",
  no_phone_number: "the contact has no phone number",
  agent_number_missing: "the agent has no phone number",
  no_calling_window: "the agent has no calling hours",
  agent_calling_disabled: "outbound calling is off for this agent",
  agent_unavailable: "the agent no longer exists",
  feature_disabled: "outbound calling is off for this workspace",
  call_setup_failed: "the call was answered but could not be connected",
  dial_failed: "the call could not be placed",
};

export const nextActionLabel: Record<
  CustomerInteractionNextAction["type"],
  string
> = {
  scheduled: "Call scheduled",
  paused: "Call paused",
  monitoring: "Monitoring",
  monitoring_paused: "Outreach paused",
};

/** What the time under a next action is: the call, the deadline, or the agent's next turn. */
export const nextActionTimePrefix: Record<
  CustomerInteractionNextAction["type"],
  string
> = {
  scheduled: "",
  paused: "Enable by ",
  monitoring: "Checks back by ",
  monitoring_paused: "Enable by ",
};

/**
 * "Aug 3, 11:30 AM PDT" — in the company's timezone, not the reader's, because
 * calling hours are the company's and a time in some other zone can't be
 * checked against them.
 */
export function formatCallActivityTimestamp(
  timestamp: string,
  timezone: string,
): string {
  const dateTime = DateTime.fromISO(timestamp).setZone(timezone);
  return dateTime.isValid ? dateTime.toFormat("MMM d, h:mm a ZZZZ") : timestamp;
}

const EmptyCell: FC = () => <span className="text-muted-foreground">--</span>;
